import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
  Alert,
  Stack,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useStatistics } from '../../hooks/useStatistics';
import { AIUsageLog } from '../../types/admin';

export const AIUsageLogs: React.FC = () => {
  const {
    aiUsageLogs,
    totalCount,
    currentPage,
    pageSize,
    filters,
    loading,
    error,
    fetchAIUsageLogs,
    setPage,
    setFilters,
    clearError,
  } = useStatistics();

  const [searchQuery, setSearchQuery] = useState(filters.userSearch || '');
  const [expertFilter, setExpertFilter] = useState(filters.expertAI || '');
  const [dateRange, setDateRange] = useState({
    start: filters.dateRange?.start || '',
    end: filters.dateRange?.end || '',
  });
  
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AIUsageLog | null>(null);

  useEffect(() => {
    fetchAIUsageLogs();
  }, [fetchAIUsageLogs]);

  const handleSearch = () => {
    setFilters({
      ...filters,
      userSearch: searchQuery.trim() || undefined,
      expertAI: expertFilter.trim() || undefined,
      dateRange: dateRange.start && dateRange.end
        ? { start: dateRange.start, end: dateRange.end }
        : undefined,
    });
  };

  const handleViewDetail = (log: AIUsageLog) => {
    setSelectedLog(log);
    setDetailDialogOpen(true);
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage + 1);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ja-JP');
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}時間${mins}分`;
    }
    return `${mins}分`;
  };

  const getExpertChip = (expertName: string) => {
    const colorMap: { [key: string]: 'primary' | 'secondary' | 'success' | 'warning' } = {
      '宅建士AI': 'primary',
      '営業AI': 'secondary',
      '法務AI': 'success',
      '市場分析AI': 'warning',
    };
    
    return (
      <Chip
        label={expertName}
        color={colorMap[expertName] || 'default'}
        size="small"
        variant="outlined"
      />
    );
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 3 }}>
        AI利用ログ・分析
      </Typography>

      {error && (
        <Alert severity="error" onClose={clearError} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* 検索・フィルター */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          検索・フィルター
        </Typography>
        
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="ユーザー名"
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ flex: 1, minWidth: 200 }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />,
              }}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            
            <TextField
              label="専門家AI名"
              variant="outlined"
              value={expertFilter}
              onChange={(e) => setExpertFilter(e.target.value)}
              sx={{ flex: 1, minWidth: 200 }}
            />
          </Stack>
          
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="end">
            <TextField
              label="開始日"
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 150 }}
            />
            
            <TextField
              label="終了日"
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 150 }}
            />
            
            <Button
              variant="contained"
              onClick={handleSearch}
              startIcon={<FilterListIcon />}
              disabled={loading}
            >
              検索
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* AI利用ログテーブル */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>日時</TableCell>
              <TableCell>ユーザー名</TableCell>
              <TableCell>専門家AI</TableCell>
              <TableCell>メッセージ数</TableCell>
              <TableCell>セッション時間</TableCell>
              <TableCell>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              // Loading skeleton
              Array.from({ length: 10 }).map((_, index) => (
                <TableRow key={index}>
                  {Array.from({ length: 6 }).map((_, cellIndex) => (
                    <TableCell key={cellIndex}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              aiUsageLogs.map((log) => (
                <TableRow key={log.id} hover>
                  <TableCell>{formatDate(log.createdAt)}</TableCell>
                  <TableCell>{log.userName}</TableCell>
                  <TableCell>{getExpertChip(log.expertAIName)}</TableCell>
                  <TableCell>{log.messageCount}件</TableCell>
                  <TableCell>{formatDuration(log.sessionDuration)}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      onClick={() => handleViewDetail(log)}
                      startIcon={<VisibilityIcon />}
                    >
                      詳細
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        
        <TablePagination
          component="div"
          count={totalCount}
          page={currentPage - 1}
          onPageChange={handlePageChange}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[20]}
          labelDisplayedRows={({ from, to, count }) => 
            `${from}–${to} / ${count}件`
          }
          labelRowsPerPage="表示件数:"
        />
      </TableContainer>

      {/* セッション詳細ダイアログ */}
      <Dialog 
        open={detailDialogOpen} 
        onClose={() => setDetailDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>AIセッション詳細</DialogTitle>
        <DialogContent>
          {selectedLog && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="h6" gutterBottom>
                基本情報
              </Typography>
              <Stack spacing={1} sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>ユーザー:</strong> {selectedLog.userName}
                </Typography>
                <Typography variant="body2">
                  <strong>専門家AI:</strong> {selectedLog.expertAIName}
                </Typography>
                <Typography variant="body2">
                  <strong>セッション開始:</strong> {formatDate(selectedLog.createdAt)}
                </Typography>
                <Typography variant="body2">
                  <strong>メッセージ数:</strong> {selectedLog.messageCount}件
                </Typography>
                <Typography variant="body2">
                  <strong>セッション時間:</strong> {formatDuration(selectedLog.sessionDuration)}
                </Typography>
              </Stack>

              {selectedLog.sessionSummary && (
                <>
                  <Typography variant="h6" gutterBottom>
                    セッション概要
                  </Typography>
                  <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                    <Typography variant="body2">
                      {selectedLog.sessionSummary}
                    </Typography>
                  </Paper>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>
            閉じる
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};